<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'vuex';
import * as types from 'dashboard/store/mutation-types';
import InboxBusinessHoursAPI from 'dashboard/api/inboxBusinessHours';
import { useAccount } from 'dashboard/composables/useAccount';
import BusinessHoursEditor from '../businessHours/components/BusinessHoursEditor.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const { currentAccount } = useAccount();
const isSaving = ref(false);

const accountReferenceText = computed(() => {
  const accountTimezone = currentAccount.value?.settings?.account_timezone;
  return t('INBOX_MGMT.BUSINESS_HOURS.REFERENCE', {
    timezone: accountTimezone || 'UTC',
  });
});

const saveBusinessHours = async payload => {
  isSaving.value = true;
  try {
    const response = await InboxBusinessHoursAPI.update(
      props.inbox.id,
      payload
    );
    store.commit(types.default.EDIT_INBOXES, response.data);
    useAlert(t('INBOX_MGMT.BUSINESS_HOURS.API.SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.BUSINESS_HOURS.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const resetFromAccount = async () => {
  isSaving.value = true;
  try {
    const response = await InboxBusinessHoursAPI.reset(props.inbox.id);
    store.commit(types.default.EDIT_INBOXES, response.data);
    useAlert(t('INBOX_MGMT.BUSINESS_HOURS.API.RESET_SUCCESS'));
  } catch {
    useAlert(t('INBOX_MGMT.BUSINESS_HOURS.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="w-full max-w-5xl">
    <BusinessHoursEditor
      :value="inbox"
      :is-saving="isSaving"
      show-reset-action
      :reference-text="accountReferenceText"
      :save-label="$t('INBOX_MGMT.SETTINGS_POPUP.SAVE_CHANGES')"
      :reset-label="$t('INBOX_MGMT.BUSINESS_HOURS.RESET')"
      @save="saveBusinessHours"
      @reset="resetFromAccount"
    />
  </div>
</template>
